import React, { useState, useRef, useEffect, useCallback } from 'react';
import { styled } from 'styled-components';
import { Button, FlexBoxHorizontal, FlexBoxVertical, FlexItem, FlexBox, Label, TranslateTypography, TextField } from '../../atoms';
import { LuImagePlus } from 'react-icons/lu';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import palette from '../../../utils/colorPalette';
import i18n from '../../../utils/i18n'
import { FaRegCopy } from "react-icons/fa";
import { useUser } from '../../../contexts/UserContext';

export const CadDataSub = ({ data, changeImg, onGraphicSaved }) => {
  const language = i18n.language;
  const [imgSrc, setImgSrc] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showToast, setShowToast] = useState(false); // 토스트 표시 상태
  const [imgFallback, setImgFallback] = useState(0); // 0: 원본, 1: PLM, 2: 아이콘
  const [graphicList, setGraphicList] = useState([]);
  const [selectedGraphicCodes, setSelectedGraphicCodes] = useState([]);
  const [showGraphicSelector, setShowGraphicSelector] = useState(false);
  const [graphicSearchKeyword, setGraphicSearchKeyword] = useState('');
  const imgRef = useRef(null);
  const graphicSelectorRef = useRef(null);
  const { user, permissions, hasPermission, loading: userLoading } = useUser();
  const isPartSelected = !!data?.part_cd;


  useEffect(() => {
    if (data?.img) {
      // 캐시 방지 쿼리 파라미터 추가 (data.img 에 이미 ?timestamp 가 붙어오므로, 기존 쿼리스트링 제거 후 하나만 부여)
      setImgSrc(`${data.img.split('?')[0]}?timestamp=${new Date().getTime()}`);
      setImgFallback(0);
    }
  }, [data]); // data 변경 감지

  const fetchGraphicMapping = useCallback(async () => {
    if (!data?.part_cd) {
      setGraphicList([]);
      setSelectedGraphicCodes([]);
      setShowGraphicSelector(false);
      return;
    }

    try {
      const response = await fetch('/api/cad-data/graphic-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brd_cd: data?.brd_cd,
          sesn: data?.sesn,
          part_cd: data?.part_cd,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || '그래픽 코드 조회 실패');
      }

      setGraphicList(result.graphicList || []);
      setSelectedGraphicCodes(result.mappedGraphicCodes || []);
    } catch (error) {
      console.error(error);
      setGraphicList([]);
      setSelectedGraphicCodes([]);
    }
  }, [data?.brd_cd, data?.sesn, data?.part_cd]);

  useEffect(() => {
    fetchGraphicMapping();
  }, [fetchGraphicMapping]);

  useEffect(() => {
    const handleOutsideClick = async (event) => {
      if (!showGraphicSelector) return;
      if (graphicSelectorRef.current && !graphicSelectorRef.current.contains(event.target)) {
        setShowGraphicSelector(false);
        await fetchGraphicMapping();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showGraphicSelector, fetchGraphicMapping]);

  const handleImgError = (e) => {
    if (imgFallback === 0) {
      setImgFallback(1);
      e.target.src = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${data?.brd_cd}/${data?.sesn}/${data?.part_cd}.jpg?timestamp=${Date.now()}`;
    } else if (imgFallback === 1) {
      setImgFallback(2);
      e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 압축 함수 수정
  const compressImage = async (file) => {
    // 300KB = 300 * 1024 bytes
    if (file.size <= 300 * 1024) {
      return file; // 300KB 이하면 원본 반환
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.8  
          );
        };
      };
    });
  };

  const BLOCKED_SESNS = [];

  const handleUpload = async () => {
    if (!data || !data['part_cd']) {
      alert('이미지를 업로드할 상품을 선택하세요.');
      return;
    }

    const sesn = data['sesn'] ? String(data['sesn']).toUpperCase() : '';
    if (BLOCKED_SESNS.includes(sesn)) {
      alert(`업로드 불가\n${sesn} 이미지는 PLM 대지 이미지가 자동 적용됩니다.`);
      return;
    }

    if (!selectedFile) {
      alert('업로드할 파일을 선택하세요.');
      return;
    }
  
    const formData = new FormData();

    // 원본 파일과 압축 파일 크기 비교를 위해 압축 파일 생성
    const compressedFile = await compressImage(selectedFile);

    // 원본 파일 추가
    formData.append('originalFile', selectedFile);
    // 압축 파일 추가
    formData.append('compressedFile', compressedFile);

    // 메타데이터 추가
    formData.append('part_cd', data['part_cd']);
    formData.append('sesn', data['sesn']);
    formData.append('brd_cd', data['brd_cd']);
  
    try {
      const response = await fetch('/api/s3-upload', {
        method: 'POST',
        body: formData,
      });
      const result = await response.text();
      alert(result);
  
      // 업로드 성공 시 이미지 초기화
      handleDeleteImage();

      const newImgSrc = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${data['brd_cd']}/${data['sesn']}/${data['part_cd']}.jpg?timestamp=${Date.now()}`;
      setImgFallback(0);
      setImgSrc(newImgSrc);

      // 상위 컴포넌트에 업데이트된 imgSrc 전달
      if (changeImg) {
        changeImg(newImgSrc);
      }
    } catch (error) {
      console.error('업로드 오류:', error);
      alert('파일 업로드에 실패했습니다.');
    }
  };

  const fileInputRef = React.useRef();

  const handleClick = () => {
    fileInputRef.current.click();
  };

  // 이미지 삭제 핸들러
  const handleDeleteImage = () => {
    setSelectedFile(null);
    setPreviewImage(null);

    // 파일 입력 필드 값 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // 동일한 파일을 다시 선택할 수 있도록 초기화
    }
  };

   // 드래그 앤 드롭 및 붙여넣기 핸들러
   const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handlePaste = (event) => {
    const items = event.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          setSelectedFile(file);
          const reader = new FileReader();
          reader.onload = (e) => {
            setPreviewImage(e.target.result);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const toggleGraphicCode = (graphicCode) => {
    setSelectedGraphicCodes((prev) => {
      if (prev.includes(graphicCode)) {
        return prev.filter((code) => code !== graphicCode);
      }
      return [...prev, graphicCode];
    });
  };

  const saveGraphicMapping = async () => {
    if (!data?.part_cd) {
      alert('품번을 먼저 선택해주세요.');
      return;
    }

    try {
      const response = await fetch('/api/cad-data/save-graphic-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brd_cd: data?.brd_cd,
          sesn: data?.sesn,
          part_cd: data?.part_cd,
          graphicCodes: selectedGraphicCodes,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || '그래픽 코드 저장 실패');
      }

      alert('그래픽 코드 매핑이 저장되었습니다.');
      onGraphicSaved && onGraphicSaved(data, selectedGraphicCodes);
      setShowGraphicSelector(false);
      await fetchGraphicMapping();
    } catch (error) {
      console.error(error);
      alert(error.message || '그래픽 코드 저장 중 오류가 발생했습니다.');
    }
  };

  const filteredGraphicList = graphicList.filter((item) => {
    if (!graphicSearchKeyword.trim()) return true;
    return `${item.graphic_cd || ''} ${item.graphic_nm || ''}`
      .toLowerCase()
      .includes(graphicSearchKeyword.trim().toLowerCase());
  });

  return (
    <FlexBoxVertical style={{justifyContent: 'flex-start'}}>
      {/* 상단부: 코드 및 브랜드 */}
      <FlexBoxHorizontal style={{ height: '40px', margin: '10px 10px 0 10px' }}>
        <Label style={{ width: '45%', margin: '3px' }}>
          <TranslateTypography variant="content2">
            {data && data['part_cd'] ? data['part_cd'] : '품번'}
          </TranslateTypography>
        </Label>
        <Label style={{ width: '45%', margin: '3px' }}>
          <TranslateTypography variant="content2">
            {data && data['fabric'] ? data['fabric'] : '패브릭'}
          </TranslateTypography>
        </Label>
      </FlexBoxHorizontal>

      {/* description */}
      <FlexItem style={{ textAlign: 'center', margin: '10px', fontWeight: 'bold', color: '#353339' }}>
        <TranslateTypography variant="content1">
          {language === 'ko' 
            ? (data?.description || '제품명')
            : (data?.description_eng || '제품명')}
        </TranslateTypography>
      </FlexItem>

      {/* 상세정보 */}
      <FlexBoxVertical style={{height : '50%', borderBottom: `1.5px solid ${palette.border.default}`}}>
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('domain') && data['domain'] !== null  ? data['domain'] : ' '}
            placeholder="도메인"
            readOnly
            width={'140px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('set_up_style_no') && data['set_up_style_no'] !== null ? data['set_up_style_no'] : ' '}
            placeholder="셋업스타일넘버"
            readOnly
            width={'140px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('fit') && data['fit'] !== null  ? data['fit'] : ' '}
            placeholder="핏"
            readOnly
            width={'140px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('tag') && data['tag'] !== null  ? data['tag'].toLocaleString() : ' '}
            placeholder="택가"
            readOnly
            width={'140px'}
          />
        </FlexBoxHorizontal>
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('life_style') && data['life_style'] !== null  ? data['life_style'] : ' '}
            placeholder="라이프스타일"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('level') && data['level'] !== null   ? data['level'] : ' '}
            placeholder="레벨"
            readOnly
            width={'200px'}
          />
        </FlexBoxHorizontal>

        {/* 이미지 박스 */}
        <FlexBox
          style={{
            width: '350px',
            height: '150px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* <Button 
            style={{
              position: 'absolute', 
              top: '0px', 
              right: '0px', 
              width: '45px', 
              height: '24px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#666',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              padding: '0 6px'
            }}
            onClick={() => {
              navigator.clipboard.writeText(imgSrc);
              setShowToast(true);
              setTimeout(() => {
                setShowToast(false);
              }, 1500);
            }}
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <FaRegCopy style={{ fontSize: '8px' }} />
                <span>URL</span>
              </div>
            }
            variant="caption"
          /> */}
          <img
          ref={imgRef}
          src={imgSrc}
          alt="Content"
          onError={handleImgError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
        </FlexBox>

        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('designer') && data['designer'] !== null   ? data['designer'] : ' '}
            placeholder="디자이너"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('vendor') && data['vendor'] !== null   ? data['vendor'] : ' '}
            placeholder="협력사"
            readOnly
            width={'200px'}
          />
        </FlexBoxHorizontal>
        <FlexBox style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('color') && data['color'] !== null ? data['color'] : ' '}
            placeholder="컬러"
            readOnly
            width={'100%'}
          />
        </FlexBox>
        <div ref={graphicSelectorRef} style={{ width: '90%', marginTop: '8px', marginBottom: '18px', position: 'relative' }}>
          {isPartSelected && selectedGraphicCodes.length > 0 && (
            <label
              style={{
                position: 'absolute',
                left: '8px',
                top: '-7px',
                background: 'white',
                fontSize: '0.65rem',
                color: '#666',
                padding: '0 2px',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              그래픽 코드
            </label>
          )}
          <div
            onClick={() => {
              if (!isPartSelected) return;
              setShowGraphicSelector((prev) => !prev);
            }}
            style={{
              width: '100%',
              minHeight: '30px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px 8px',
              fontSize: '0.8rem',
              color: selectedGraphicCodes.length > 0 ? '#353339' : '#999',
              cursor: isPartSelected ? 'pointer' : 'not-allowed',
              backgroundColor: isPartSelected ? '#fff' : '#f5f5f5',
              boxSizing: 'border-box',
              opacity: isPartSelected ? 1 : 0.7,
            }}
          >
            {!isPartSelected
              ? '품번 선택 후 사용 가능'
              : (selectedGraphicCodes.length > 0 ? selectedGraphicCodes.join(', ') : '그래픽 코드')}
          </div>
          {isPartSelected && showGraphicSelector && (
            <div
              style={{
                position: 'absolute',
                top: '38px',
                left: 0,
                right: 0,
                zIndex: 10,
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: '#fff',
                maxHeight: 'calc(100vh - 380px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.08)',
                padding: '34px 8px 4px 8px',
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '6px',
                  left: '10px',
                  right: '142px',
                  zIndex: 11,
                }}
              >
                <GraphicSearchInput
                  type="text"
                  value={graphicSearchKeyword}
                  onChange={(e) => setGraphicSearchKeyword(e.target.value)}
                  placeholder="코드 또는 이름 검색"
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '10px',
                  display: 'flex',
                  gap: '0px',
                  zIndex: 11,
                }}
              >
                <Button
                  style={{ padding: '2px 8px', fontSize: '9px', minHeight: '22px', lineHeight: 1, marginLeft: '4px' }}
                  onClick={saveGraphicMapping}
                  label="저장"
                >
                  저장
                </Button>
              </div>
              {filteredGraphicList.length === 0 ? (
                <div style={{ padding: '10px 6px', color: '#888', fontSize: '12px' }}>조회된 그래픽 코드가 없습니다.</div>
              ) : (
                filteredGraphicList.map((item) => {
                  const code = item.graphic_cd;
                  const checked = selectedGraphicCodes.includes(code);
                  return (
                    <div
                      key={code}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%' }}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleGraphicCode(code)}
                          sx={{
                            padding: '4px',
                            color: '#666666',
                            '&.Mui-checked': {
                              color: '#666666',
                            },
                          }}
                        />
                        <span style={{ fontSize: '12px', color: '#444', flexShrink: 0 }}>{code}</span>
                        <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.graphic_nm}>{item.graphic_nm}</span>
                        {item.use_yn === 'N' && (
                          <span style={{ fontSize: '10px', color: '#aaa', marginLeft: '6px', flexShrink: 0 }}>미사용</span>
                        )}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </FlexBoxVertical>

      {/* 업로드 박스 - cadDashboard-image edit 권한 필요 */}
      {!hasPermission('cadDashboard-image', 'edit') && (
        <div style={{ margin: '10px 20px 0 20px', width: '90%', height: '300px' }} />
      )}
      {hasPermission('cadDashboard-image', 'edit') && (
        <>
          <UploadBox
            $hasPreview={!!previewImage}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onPaste={handlePaste}
          >
            <FlexBoxVertical>
              {previewImage ? (
                <ImageContainer>
                  <img
                    src={previewImage}
                    alt="미리보기"
                    className="preview-image"
                  />
                  <CloseIcon
                    onClick={handleDeleteImage}
                    className="delete-icon"
                  />
                </ImageContainer>
              ) : (
                <>
                  <LuImagePlus style={{ width: '20%', height: '20%', color: 'rgb(218, 222, 224)' }} />
                  <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)', margin: '5px' }}>
                    드래그하거나 Ctrl + V를 입력하여
                  </TranslateTypography>
                  <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)' }}>
                    이미지를 붙여넣으세요
                  </TranslateTypography>
                </>
              )}
            </FlexBoxVertical>
          </UploadBox>

          {/* 파일 선택 및 업로드 버튼 */}
          <FlexBoxHorizontal style={{ height: 'auto', padding: '10px 0' }}>
            <input
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <Button style={{ marginRight: '10px', padding: '6px' }} onClick={handleClick} label="파일 선택">
              파일 선택
            </Button>
            <Button style={{ padding: '6px' }} onClick={handleUpload} label="저장">
              업로드
            </Button>
          </FlexBoxHorizontal>
        </>
      )}

      {/* 토스트 메시지 */}
      {showToast && (
        <Toast>
          ✔ COPIED
        </Toast>
      )}
    </FlexBoxVertical>
  );
};


// UploadBox 스타일 컴포넌트
const UploadBox = styled(FlexBox)`
  margin: 10px 20px 0 20px;
  border: 2px dashed rgb(218, 222, 224);
  width: 90%;
  height: 300px; /* 고정 높이 설정 */
  border-radius: 10px;
  transition: background-color 0.3s ease;
  /* 프리뷰 이미지가 없을 때만 호버 배경색 변경 */
  &:hover {
    background-color: ${({ $hasPreview }) => ($hasPreview ? 'transparent' : 'rgba(218, 222, 224, 0.2)')};
  }
`;

// ImageContainer 스타일 컴포넌트
const ImageContainer = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
  overflow: hidden;
  max-height: 260px; /* 최대 높이 설정 */

  .preview-image {
    width:100%;
    height: 100%;
    object-fit: cover;
    transition: filter 0.3s ease;
    margin: 5px;
  }

  .delete-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    cursor: pointer;
    display: none;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 5px;
  }

  &:hover .preview-image {
    filter: brightness(50%);
  }

  &:hover .delete-icon {
    display: block;
  }
`;

// 토스트 스타일 컴포넌트 추가
const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: rgb(97, 97, 230);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const GraphicSearchInput = styled.input`
  width: 100%;
  height: 24px;
  border: 1px solid #d7d9dd;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 11px;
  color: #353339;
  background-color: #fafbfc;
  box-sizing: border-box;
  outline: none;
  box-shadow: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:focus {
    border: 1px solid #c6c9cf;
    background-color: #ffffff;
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: #a0a4ab;
  }
`;
